import React, { useEffect } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, CircularProgress, Typography } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { fetchDepartements } from '../reduxStore/DepartementSlice';
import { fetchAllowedDepartements } from '../reduxStore/ScopeSlice';
import { setSelectedDepartement, setAllMode } from '../reduxStore/AppSlice';
import PublicIcon from '@mui/icons-material/Public';
import { logout } from '../reduxStore/AuthSlice';
import { Departement } from '../models/models';
import AppPage from '../components/layout/AppPage';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppSurface from '../components/layout/AppSurface';
import AppOutlineButton from '../components/layout/AppOutlineButton';

const DepartementSelectionPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { departements, loading, error } = useSelector((state: RootState) => state.DepartementSlice);
  const { allowedDepartements } = useSelector((state: RootState) => state.ScopeSlice);

  useEffect(() => {
    dispatch(fetchDepartements());
    dispatch(fetchAllowedDepartements());
  }, [dispatch]);

  const handleSelect = (dept: Departement) => {
    dispatch(setSelectedDepartement(dept));
    navigate('/');
  };

  const handleSelectAll = () => {
    dispatch(setAllMode());
    navigate('/');
  };

  return (
    <AppPage maxWidth="lg">
      <AppPageHeader
        title="Sélection du département"
        subtitle="Choisissez le département dans lequel vous souhaitez travailler"
        icon={<ApartmentIcon />}
        actions={
          <AppOutlineButton
            startIcon={<LogoutIcon />}
            onClick={() => dispatch(logout())}
            sx={{
              borderColor: '#fecdca', color: '#b42318',
              '&:hover': { borderColor: '#b42318', bgcolor: '#fef3f2' },
            }}
          >
            Se déconnecter
          </AppOutlineButton>
        }
      />

      <AppSurface padding={{ xs: 2, md: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box textAlign="center" py={4}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => dispatch(fetchDepartements())}>
              Réessayer
            </Button>
          </Box>
        ) : departements.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Aucun département n'existe encore. Créez-en un pour commencer.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/departement')}>
              Créer un département
            </Button>
          </Box>
        ) : allowedDepartements.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Aucun département ne vous est accessible. Contactez votre administrateur.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={3} justifyContent="center">
            {/* Carte "Tous les départements" : visible uniquement si l'utilisateur
                n'a AUCUN dept blacklisté (allowedDepartements = tous les depts).
                Note : `length === 0` signifierait blacklist totale (à l'inverse). */}
            {departements.length > 0 && allowedDepartements.length === departements.length && (
              <Card
                sx={{
                  width: 200,
                  height: 180,
                  boxShadow: 'none',
                  borderRadius: '16px',
                  border: '1px solid #c7d2fe',
                  bgcolor: '#eef4ff',
                  display: 'flex',
                  transition: 'transform .15s ease, box-shadow .15s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(16,24,40,0.08)' },
                }}
              >
                <CardActionArea
                  onClick={handleSelectAll}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                    <PublicIcon fontSize="large" sx={{ mb: 0.5, color: '#2563eb' }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#101828' }}>
                      Tous les départements
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#667085' }} mt={1}>
                      Vue consolidée multi-départements
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
            {allowedDepartements.map((dept) => (
              <Card
                key={dept.id}
                sx={{
                  width: 200,
                  height: 180,
                  boxShadow: 'none',
                  borderRadius: '16px',
                  border: '1px solid #e4e7ec',
                  display: 'flex',
                  transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                  '&:hover': { borderColor: '#c7d2fe', transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(16,24,40,0.08)' },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelect(dept)}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#101828' }}>
                      {dept.nom}
                    </Typography>
                    {dept.description && (
                      <Typography variant="body2" sx={{ color: '#667085' }} mt={1}>
                        {dept.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </AppSurface>
    </AppPage>
  );
};

export default DepartementSelectionPage;
